import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useUiStore } from '../../../shared/hooks'
import { notificationsApi } from '../notifications.api'
import { playNotificationSound, unlockNotificationSound } from '../sound'

/** Unseen/unread counts, polled every 20 s while the tab is visible and on focus (ADR-047). */
export function useNotificationSummary() {
  return useQuery({
    queryKey: ['notifications', 'summary'],
    queryFn: notificationsApi.summary,
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  })
}

/** Plays the chime when a notification newer than the last one seen appears. */
export function useNotificationSound(latestAt: string | null | undefined) {
  const enabled = useUiStore((s) => s.notificationSound)
  const last = useRef<string | null | undefined>(undefined)

  useEffect(() => unlockNotificationSound(), [])

  useEffect(() => {
    if (latestAt === undefined) return
    // The first value is the baseline: no sound for notifications that already existed.
    if (
      last.current !== undefined &&
      latestAt &&
      latestAt > (last.current ?? '') &&
      enabled
    )
      playNotificationSound()
    last.current = latestAt
  }, [latestAt, enabled])
}

export function useNotificationList(
  open: boolean,
  locale: string,
  latestAt?: string | null,
) {
  return useQuery({
    queryKey: ['notifications', 'list', locale, latestAt ?? null],
    queryFn: () => notificationsApi.list(locale),
    enabled: open,
  })
}
